/*
 * ProjectForm.tsx : Un formulaire pour ajouter ou modifier un projet.
 */

// Import React
import React from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

// Import FontAwesome
import {
	faBox,
	faBriefcase,
	faChartLine,
	faClipboardList,
	faCoins,
	faFileContract,
	faFileInvoiceDollar,
	faInfoCircle,
	faMoneyBills,
	faUserTie,
	faReceipt,
} from '@fortawesome/free-solid-svg-icons';

// Import Communs et Layout
import {
	DefinitionDescriptionField,
	DefinitionTermField,
	finishFormSave,
	FooterTotal,
	FormSaveActions,
	InputField,
	RelatedItemsManager,
	RelatedItemsTable,
	SelectField,
	StatusCard,
	TextAreaField,
	useFormDirty,
} from '../../../common';
import { useWorkflowStatusConfig } from '../../../common/hooks/useWorkflowStatusConfig';
import CardHeader from '../../../layout/CardHeader';
import CardSection from '../../../layout/CardSection';

// Import Project
import {
	PROJECT_STATUS_CONFIG,
	ProjectBudget,
	ProjectData,
} from '../types/types';
import { projectsApi } from '../services/projectsApi';

// Imports Éléments logistiques
import { LogisticElementData } from 'features/logisticElements/types/types';
import { logisticElementsApi } from 'features/logisticElements/services/logisticElementsApi';

// Imports Produits (pour la refacturation des dépenses)
import { productsApi } from 'features/products/services/productsApi';

// Imports Dépenses (pour la mise à jour après création de l'élément logistique de refacturation)
import { expensesApi } from 'features/expenses/services/expensesApi';

// Imports Activités
import { ActivityData } from 'features/activities/types/types';
import { activitiesApi } from 'features/activities/services/activitiesApi';

// Imports Acomptes
import { AdvanceData, advancesApi } from 'features/advances';

// Import Commandes client
import { CustomerOrderData, customerOrdersApi } from 'features/customerOrders';

// Import Factures client
import {
	CustomerInvoiceData,
	customerInvoicesApi,
} from 'features/customerInvoices';

// Import Devis client
import { quotesApi } from 'features/quotes/services/quotesApi';
import { QUOTE_STATUS_CONFIG } from 'features/quotes/types/types';

function editableProject(project: ProjectData) {
	const editable = { ...project };
	delete editable.status;
	return editable;
}

const QUOTE_STATUS_LABELS: Record<string, string> = Object.fromEntries(
	QUOTE_STATUS_CONFIG.steps.map((step) => [step.key, step.label])
);

const formatDay = (value?: string | null): string => {
	if (!value) return '—';
	const date = new Date(value);
	return Number.isNaN(date.getTime()) ? '—' : date.toLocaleDateString('fr-FR');
};

const formatRange = (start?: string | null, end?: string | null): string =>
	`Du ${formatDay(start)} au ${formatDay(end)}`;

const formatEuros = (value: number): string =>
	new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(
		value
	);

const BudgetBar: React.FC<{
	current: number;
	total: number;
	loading: boolean;
	compact?: boolean;
	className?: string;
}> = ({ current, total, loading, compact = false, className }) => {
	if (loading) {
		return (
			<div
				className={`w-full animate-pulse rounded bg-gray-200 ${compact ? 'h-6' : 'h-8'} ${className ?? ''}`}
			/>
		);
	}

	const pct = total > 0 ? Math.round((current / total) * 100) : null;
	const barColor =
		total <= 0
			? 'bg-gray-300'
			: current > total
				? 'bg-red-500'
				: current < total
					? 'bg-orange-400'
					: 'bg-green-500';
	const textColor =
		total <= 0 || pct === null
			? 'text-gray-400'
			: current > total
				? 'text-red-500'
				: current < total
					? 'text-orange-500'
					: 'text-green-600';

	const barHeight = compact ? 'h-1.5' : 'h-2';

	return (
		<div className={`flex min-w-0 flex-col gap-1 py-1 ${className ?? ''}`}>
			<div className={`${barHeight} overflow-hidden rounded-full bg-gray-200`}>
				<div
					className={`${barHeight} rounded-full transition-all ${barColor}`}
					style={{ width: `${Math.min(pct ?? 0, 100)}%` }}
				/>
			</div>
			<div className="flex justify-between gap-2 text-xs text-gray-500">
				<span>
					<span className="font-semibold">{formatEuros(current)}</span>
					{' / '}
					{total > 0 ? formatEuros(total) : '—'}
				</span>
				{pct !== null && (
					<span className={`font-semibold ${textColor}`}>{pct}%</span>
				)}
			</div>
		</div>
	);
};

// Composant ProjectForm
const ProjectForm: React.FC = () => {
	// Hook de Chargement
	const [loading, setLoading] = React.useState(true);
	const [baselineReady, setBaselineReady] = React.useState(false);

	// Hook de Navigation et Paramètres de navigation
	const navigate = useNavigate();
	const { id } = useParams<{ id: string }>();
	const statusConfig = useWorkflowStatusConfig(
		'project',
		PROJECT_STATUS_CONFIG
	);
	const [searchParams] = useSearchParams();
	const quoteIdParam = searchParams.get('quote') || '';

	// Hook utilisateur courant
	const userId = localStorage.getItem('userId');
	const userName = localStorage.getItem('name');
	const userFirstName = localStorage.getItem('firstName');

	// Hook de gestion du formulaire
	const [formData, setFormData] = React.useState<ProjectData>({
		id: 0,
		status: 'DRAFT',
		referenceNumber: '',
		comments: '',
		createdBy: userId
			? {
					id: Number(userId),
					name: userName || '',
					firstName: userFirstName || '',
				}
			: undefined,
		createdAt: new Date()
			.toISOString()
			.replace('Z', '+00:00') as unknown as Date,
	});
	// L'état est enregistré dès le clic sur le bloc État.
	const { isDirty, markClean } = useFormDirty(
		editableProject(formData),
		baselineReady
	);

	// Hook pour les options de sélection des devis (quotes)
	const [quoteOptions, setQuoteOptions] = React.useState<
		{ key: string; label: string }[]
	>([]);
	const [loadingQuotes, setLoadingQuotes] = React.useState(true);

	// Hook pour le token d'authentification
	const token = localStorage.getItem('token') || '';

	// Loading : Fetch des données et remplissage du formulaire (une fois le composant monté, si un id est présent)
	React.useEffect(() => {
		// Vérifier la présence du token d'authentification
		const token = localStorage.getItem('token');
		if (!token) return;

		if (id) {
			projectsApi
				.get(Number(id), token)
				.then((data) => {
					setFormData(data);
					// Préremplir les selects
					if (data.quote) {
						setQuoteOptions([
							{
								key: data.quote.id.toString(),
								label: data.quote.referenceNumber,
							},
						]);
					}
				})
				.catch((error) => console.error(error))
				.finally(() => {
					setLoading(false);
					setBaselineReady(true);
				});
		} else {
			// Si pas d'id, on arrête le loading directement
			setLoading(false);
			setBaselineReady(true);
		}
	}, [id]);

	const [budget, setBudget] = React.useState<ProjectBudget | null>(null);
	const [budgetLoading, setBudgetLoading] = React.useState(false);

	// Le budget ne sert qu'à la consultation : il arrive après les données nécessaires à l'enregistrement.
	React.useEffect(() => {
		if (!id || loading) return;
		const authToken = localStorage.getItem('token');
		if (!authToken) return;

		let cancelled = false;
		setBudgetLoading(true);
		projectsApi
			.budget(Number(id), authToken)
			.then((data) => {
				if (!cancelled) setBudget(data);
			})
			.catch((error) => {
				if (!cancelled) console.error(error);
			})
			.finally(() => {
				if (!cancelled) setBudgetLoading(false);
			});

		return () => {
			cancelled = true;
		};
	}, [id, loading]);

	// Lazy loading : options du select Devis (quotes)
	React.useEffect(() => {
		const token = localStorage.getItem('token');
		if (!token) return;

		if (!loading && loadingQuotes) {
			quotesApi
				.listSelect(token)
				.then((items) => {
					setQuoteOptions(
						items.map((item) => ({
							key: item.id.toString(),
							label: String(item.referenceNumber),
						}))
					);
					// Si on a pas d'id (new), et qu'on a un paramètre dans l'url, on presectionne le select avec quoteIdParam
					if (!id && quoteIdParam) {
						const selectedOption = items.find(
							(item) => item.id.toString() === quoteIdParam
						);
						if (selectedOption) {
							quotesApi.get(Number(selectedOption.id), token).then((data2) => {
								// Mise à jour du formulaire avec les données du devis sélectionné
								// Si le champ Référence de l'affaire est vide, on le préremplit avec le numéro de référence du devis
								setFormData((prev) => ({
									...prev,
									quote: data2,
									referenceNumber:
										prev.referenceNumber || data2.referenceNumber,
								}));
							});
						}
					}
				})
				.catch(console.error)
				.finally(() => setLoadingQuotes(false));
		}
	}, [loading, loadingQuotes]);

	// Handle Formulaire : Soumission
	const [isSubmitting, setIsSubmitting] = React.useState(false);
	const handleSave = (shouldLeave: boolean) => {
		// Vérifier la présence du token d'authentification
		const token = localStorage.getItem('token');
		if (!token) return;
		// Indiquer que la soumission est en cours
		setIsSubmitting(true);
		const onLeave = () => navigate('/app/projects');
		// Si un id est présent, mise à jour de l'élément existant
		if (id) {
			projectsApi
				.update(Number(id), formData, token)
				.then(() => finishFormSave(shouldLeave, { onLeave, markClean }))
				.catch((error) => console.error(error))
				.finally(() => setIsSubmitting(false));
		} else {
			// Sinon, création d'un nouvel élément
			projectsApi
				.create(formData, token)
				.then((created) =>
					finishFormSave(shouldLeave, {
						onLeave,
						markClean,
						stayOnEdit: created?.id
							? () =>
									navigate(`/app/projects/${created.id}/edit`, {
										replace: true,
									})
							: undefined,
					})
				)
				.catch((error) => console.error(error))
				.finally(() => setIsSubmitting(false));
		}
	};

	const handleSubmit = (e: React.FormEvent) => {
		// Empêcher le comportement par défaut du formulaire
		e.preventDefault();
		handleSave(true);
	};

	// handle Formulaire : Changement des champs input
	const handleChange = (
		e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
	) => {
		const { name, value } = e.target;
		setFormData({ ...formData, [name]: value });
	};

	// handle Formulaire : Changement des champs input en lecture seule (ignore)
	const handleExceptionIgnore = (
		e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
	) => {
		e.preventDefault();
		return;
	};

	// handle Formulaire : Changement d'état
	const handleStatusChange = (newStatus: string) => {
		if (formData) {
			const token = localStorage.getItem('token');
			if (!token) return;
			projectsApi
				.update(formData.id, { status: newStatus }, token)
				.then((updated) => {
					setFormData((prev) =>
						prev
							? {
									...prev,
									status: updated?.status ?? newStatus,
								}
							: prev
					);
				})
				.catch((error) => console.error(error));
		}
	};

	// Handle Formulaire : Changements des selects
	const handleChangeSelect = (e: React.ChangeEvent<HTMLSelectElement>) => {
		const { name, value } = e.target;

		if (name === 'quote') {
			const selectedOption = quoteOptions.find((opt) => opt.key === value);
			if (selectedOption) {
				// Récupération des informations du devis via son api
				quotesApi
					.get(Number(selectedOption.key), token)
					.then((data) => {
						// Mise à jour du formulaire avec les données du devis sélectionné
						// Si le champ Référence de l'affaire est vide, on le préremplit avec le numéro de référence du devis
						setFormData((prev) => ({
							...prev,
							quote: data,
							referenceNumber: prev.referenceNumber || data.referenceNumber,
						}));
					})
					.catch((error) =>
						console.error(
							'Erreur lors de la récupération des données du devis :',
							error
						)
					);
			}
			return;
		}

		// Mise à jour standard pour les autres champs
		setFormData((prev) => ({ ...prev, [name]: value }));
	};

	// Fonction utilitaire : Récupérer la dernière version d'un tableau de versions
	const getLastVersionIndex = <
		T extends { versionIndex: number; status?: string },
	>(
		arr: T[]
	): T | undefined => {
		if (!arr || arr.length === 0) return undefined;

		const activeVersions = arr.filter((v) => v.status !== 'CANCELED');

		if (activeVersions.length > 0) {
			return activeVersions.reduce((prev, current) =>
				prev.versionIndex > current.versionIndex ? prev : current
			);
		}

		// fallback : toutes les versions sont annulées → on prend quand même la dernière
		return arr.reduce((prev, current) =>
			prev.versionIndex > current.versionIndex ? prev : current
		);
	};

	// Exemple d'utilisation
	const latestQuoteVersion = getLastVersionIndex(
		formData.quote?.quoteVersions ?? []
	);

	// Hook pour indiquer quelle dépense est en cours de refacturation (par son id)
	// Évite les doubles-clics et donne un retour visuel à l'utilisateur
	const [refacturationInProgress, setRefacturationInProgress] = React.useState<
		number | null
	>(null);

	/**
	 * Crée un élément logistique de refacturation pour une dépense donnée.
	 *
	 * Logique métier :
	 *  1. Recherche le produit "Prestation sous traitée refacturée" dans la liste des produits.
	 *  2. Calcule le prix de refacturation : amountHtInvoiced * (1 + penaltyAndCarePercent / 100).
	 *  3. Crée l'élément logistique via l'API.
	 *  4. Met à jour la dépense pour lui associer l'élément logistique créé.
	 *  5. Rafraîchit les données du formulaire.
	 */
	const handleCreateRefacturation = async (
		expense: NonNullable<ProjectData['expenses']>[number]
	) => {
		const authToken = localStorage.getItem('token');
		if (!authToken) return;

		setRefacturationInProgress(expense.id);

		try {
			// Étape 1 : Trouver le produit "Prestation sous traitée refacturée"
			const productsRes = await productsApi.listPaginated(authToken, {
				limit: 999999,
			});
			const productsList = Array.isArray(productsRes)
				? productsRes
				: productsRes.data;
			const refacturationProduct = productsList.find(
				(p) => p.label === 'Prestation sous traitée refacturée'
			);

			if (!refacturationProduct) {
				alert(
					'Produit "Prestation sous traitée refacturée" introuvable. Veuillez le créer dans le catalogue produits.'
				);
				return;
			}

			// Étape 2 : Calculer le prix avec majoration des peines et soins
			const baseAmount = Number(expense.amountHtInvoiced ?? 0);
			const penaltyPercent = Number(
				expense.supplierInvoice?.penaltyAndCarePercent ?? 0
			);
			const refacturatedPrice = baseAmount * (1 + penaltyPercent / 100);

			// Étape 3 : Créer l'élément logistique de refacturation
			// On récupère le taux de TVA par défaut du produit pour l'appliquer à l'ÉL
			const newLogisticElement = await logisticElementsApi.create(
				{
					project: { id: formData.id } as LogisticElementData,
					product: refacturationProduct,
					label: expense.label,
					unitPriceExclTax: refacturatedPrice,
					quantity: 1,
					taxRate: refacturationProduct.defaultTaxRate ?? null,
				} as Partial<LogisticElementData>,
				authToken
			);

			// Étape 4 : Lier la dépense à l'élément logistique créé
			await expensesApi.update(
				expense.id,
				{ logisticElement: { id: newLogisticElement.id } } as never,
				authToken
			);

			// Étape 5 : Rafraîchir les données de l'affaire pour refléter les changements
			const refreshedProject = await projectsApi.get(formData.id, authToken);
			setFormData(refreshedProject);
		} catch (error) {
			console.error(
				"Erreur lors de la création de l'élément logistique de refacturation :",
				error
			);
			alert(
				'Une erreur est survenue lors de la refacturation. Veuillez réessayer.'
			);
		} finally {
			setRefacturationInProgress(null);
		}
	};

	// Rendu du composant, le premier div est la card principale
	return (
		<div className="max-w-6xl mx-auto p-6 bg-white rounded-lg shadow-md space-y-6">
			{/* HEADER DE LA CARD */}
			<CardHeader
				icon={faBriefcase}
				title={id ? "Édition d'une affaire" : 'Nouvelle affaire'}
				createdAt={
					formData.createdAt ? formData.createdAt?.toLocaleString() : undefined
				}
				createdBy={
					formData.createdBy
						? formData.createdBy.firstName + ' ' + formData.createdBy.name
						: undefined
				}
				loading={loading}
			/>

			{/* CONTENU DE LA CARD */}
			<form onSubmit={handleSubmit} className="space-y-6">
				{/* 2 COLONNES */}
				<div className="grid grid-cols-1 md:grid-cols-2 gap-6">
					{/* COLONNE GAUCHE */}
					<div className="space-y-6">
						<div>
							<CardSection icon={faInfoCircle} title="Informations générales">
								{!formData.id || !formData.quote ? (
									<SelectField
										label="Devis original"
										selectedKey={formData.quote?.id?.toString() || ''}
										onValueChange={(val) =>
											handleChangeSelect({
												target: {
													name: 'quote',
													value: val,
												},
											} as React.ChangeEvent<HTMLSelectElement>)
										}
										options={quoteOptions}
										loading={loading}
										optionsLoading={loadingQuotes}
										entityHref={
											formData.quote?.id
												? `/app/quotes/${formData.quote.id}/manage`
												: undefined
										}
										onClear={() =>
											setFormData((prev) => ({
												...prev,
												quote: null,
											}))
										}
									/>
								) : (
									<InputField
										label="Devis original"
										name="quoteReference"
										onChange={handleExceptionIgnore}
										value={formData.quote?.referenceNumber || ''}
										loading={loading}
										placeholder="Sélectionnez un devis après la création de l'affaire"
										readOnly={true}
									/>
								)}
								<InputField
									label="Référence de l'affaire"
									name="referenceNumber"
									value={formData.referenceNumber}
									onChange={handleChange}
									required
									loading={loading}
									placeholder='ex: "M25001Q"'
								/>
								<TextAreaField
									label="Commentaires"
									name="comments"
									value={formData.comments || ''}
									onChange={handleChange}
									loading={loading}
									placeholder='ex: "Commentaires sur l&apos;affaire"'
								/>
							</CardSection>
						</div>
						<div>
							<CardSection
								icon={faUserTie}
								title="Chargé d'affaire (depuis le devis)"
							>
								{latestQuoteVersion && (
									<dl className="grid grid-cols-[auto_1fr] gap-x-2 gap-y-0.5">
										<DefinitionTermField label="Prénom :" />
										<DefinitionDescriptionField
											value={
												latestQuoteVersion.businessManager?.firstName || ''
											}
											loading={loading}
										/>
										<DefinitionTermField label="Nom :" />
										<DefinitionDescriptionField
											value={latestQuoteVersion.businessManager?.lastName || ''}
											loading={loading}
										/>

										<DefinitionTermField label="Email :" />
										<DefinitionDescriptionField
											value={latestQuoteVersion.businessManager?.email || ''}
											loading={loading}
										/>
										<DefinitionTermField label="Téléphone professionnel :" />
										<DefinitionDescriptionField
											value={
												latestQuoteVersion.businessManager?.workPhone || ''
											}
											loading={loading}
										/>
										<DefinitionTermField label="Téléphone mobile :" />
										<DefinitionDescriptionField
											value={
												latestQuoteVersion.businessManager?.mobilePhone || ''
											}
											loading={loading}
										/>
									</dl>
								)}
							</CardSection>
						</div>
					</div>

					{/* COLONNE DROITE */}
					<div className="space-y-6">
						<div>
							{/* CARD Contenant le statut de la facture */}
							<StatusCard
								status={formData?.status}
								config={statusConfig}
								onStatusChange={handleStatusChange}
								size="md"
								loading={loading}
							/>
						</div>

						<CardSection icon={faCoins} title="Budget">
							{!id ? (
								<p className="text-sm italic text-gray-400">
									Enregistrez l&apos;affaire pour consulter le budget.
								</p>
							) : (
								<dl className="grid grid-cols-[auto_1fr] items-center gap-x-2 gap-y-1">
									<DefinitionTermField
										label="Facturé :"
										tooltip="Somme HT des factures émises, y compris celles déjà payées ou partiellement payées, par rapport au devis."
									/>
									<BudgetBar
										current={budget?.billed ?? 0}
										total={budget?.quoted ?? 0}
										loading={budgetLoading || loading}
									/>
									<DefinitionTermField
										label="Services :"
										className="pl-4 text-xs"
										tooltip="Activités des factures émises, par rapport aux services du devis."
									/>
									<BudgetBar
										current={budget?.servicesBilled ?? 0}
										total={budget?.servicesQuoted ?? 0}
										loading={budgetLoading || loading}
										compact
									/>
									<DefinitionTermField
										label="Produits :"
										className="pl-4 text-xs"
										tooltip="Éléments logistiques des factures émises, par rapport aux produits du devis."
									/>
									<BudgetBar
										current={budget?.productsBilled ?? 0}
										total={budget?.productsQuoted ?? 0}
										loading={budgetLoading || loading}
										compact
									/>

									<DefinitionTermField
										label="Payé :"
										className="mt-3"
										tooltip="Somme HT des factures payées, par rapport au devis. Une facture seulement partiellement payée reste dans Facturé."
									/>
									<BudgetBar
										current={budget?.paid ?? 0}
										total={budget?.quoted ?? 0}
										loading={budgetLoading || loading}
										className="mt-3"
									/>
									<DefinitionTermField
										label="Services :"
										className="pl-4 text-xs"
										tooltip="Activités des factures payées, par rapport aux services du devis."
									/>
									<BudgetBar
										current={budget?.servicesPaid ?? 0}
										total={budget?.servicesQuoted ?? 0}
										loading={budgetLoading || loading}
										compact
									/>
									<DefinitionTermField
										label="Produits :"
										className="pl-4 text-xs"
										tooltip="Éléments logistiques des factures payées, par rapport aux produits du devis."
									/>
									<BudgetBar
										current={budget?.productsPaid ?? 0}
										total={budget?.productsQuoted ?? 0}
										loading={budgetLoading || loading}
										compact
									/>
								</dl>
							)}
						</CardSection>

						<div>
							<CardSection icon={faFileContract} title="Devis lié">
								<dl className="grid grid-cols-[auto_1fr] gap-x-2 gap-y-0.5">
									<DefinitionTermField label="Réference :" />
									<DefinitionDescriptionField
										value={formData.quote?.referenceNumber || ''}
										href={
											latestQuoteVersion?.id
												? `/app/quoteVersions/${latestQuoteVersion.id}/manage`
												: undefined
										}
										loading={loading}
									/>

									<DefinitionTermField label="Classification :" />
									<DefinitionDescriptionField
										value={formData.quote?.classification?.label || ''}
										loading={loading}
									/>

									<DefinitionTermField label="Tiers :" />
									<DefinitionDescriptionField
										value={formData.quote?.tiers?.label || ''}
										href={
											formData.quote?.tiers?.id
												? `/app/tiers/${formData.quote.tiers.id}/manage`
												: undefined
										}
										loading={loading}
									/>

									<DefinitionTermField label="Budgetisé :" />
									<DefinitionDescriptionField
										value={
											formData.quote?.budgeted
												? 'Oui'
												: formData.quote?.budgeted === false
													? 'Non'
													: ''
										}
										loading={loading}
									/>

									<DefinitionTermField label="Initialement créé par :" />
									<DefinitionDescriptionField
										value={
											formData.quote?.creator?.firstName +
												' ' +
												formData.quote?.creator?.name || ''
										}
										loading={loading}
									/>

									<DefinitionTermField label="Le :" />
									<DefinitionDescriptionField
										value={
											formData.quote?.createdAt
												? new Date(formData.quote.createdAt).toLocaleDateString(
														'fr-FR'
													)
												: ''
										}
										loading={loading}
									/>

									<DefinitionTermField label="Exercice :" />
									<DefinitionDescriptionField
										value={
											formData.quote?.quoteVersions
												? getLastVersionIndex(formData.quote?.quoteVersions)
														?.fiscalYear || undefined
												: undefined
										}
										loading={loadingQuotes}
									/>

									<DefinitionTermField label="Validité :" />
									<DefinitionDescriptionField
										value={formatRange(
											latestQuoteVersion?.startDate,
											latestQuoteVersion?.endDate
										)}
										loading={loading}
									/>

									<DefinitionTermField label="Activités :" />
									<DefinitionDescriptionField
										value={formatRange(
											latestQuoteVersion?.activityStartDate,
											latestQuoteVersion?.activityEndDate
										)}
										loading={loading}
									/>
									<DefinitionTermField label="État :" />
									<DefinitionDescriptionField
										value={
											latestQuoteVersion?.status
												? (QUOTE_STATUS_LABELS[latestQuoteVersion.status] ??
													latestQuoteVersion.status)
												: undefined
										}
										loading={loading}
									/>
								</dl>
							</CardSection>
						</div>
					</div>
				</div>

				{/* 1 COLONNES */}
				<div className="grid grid-cols-1 md:grid-cols-1 gap-6">
					{/* TABLEAU DES ACTIVITÉS LIÉES A L'AFFAIRE */}
					<CardSection icon={faChartLine} title="Activités">
						{(() => {
							// Calcul des totaux pour le pied de tableau des activités
							const activities = formData.activities || [];

							// Total des heures (somme des durationHours)
							const totalHours = activities.reduce(
								(sum, a) => sum + (a.durationHours ?? 0),
								0
							);
							const totalHoursMinutes = Math.round(totalHours * 60);
							const totalHoursDisplay =
								totalHoursMinutes > 0
									? (() => {
											const h = Math.floor(totalHoursMinutes / 60);
											const m = totalHoursMinutes % 60;
											return m > 0
												? `${h}h${String(m).padStart(2, '0')}`
												: `${h}h`;
										})()
									: '—';

							// Total HT (somme des unitPriceExclTax de chaque activité — valeur forfaitaire, pas horaire)
							const totalExclTax = activities.reduce(
								(sum, a) => sum + Number(a.unitPriceExclTax ?? 0),
								0
							);
							const totalExclTaxDisplay =
								activities.length > 0
									? new Intl.NumberFormat('fr-FR', {
											style: 'currency',
											currency: 'EUR',
										}).format(totalExclTax)
									: '—';

							const activityFooterTotals: FooterTotal<ActivityData>[] = [
								{ columnKey: 'durationHours', value: totalHoursDisplay },
								{ columnKey: 'unitPriceExclTax', value: totalExclTaxDisplay },
							];

							return (
								<RelatedItemsManager<ActivityData>
									linkedItems={activities}
									setLinkedItems={(items) =>
										setFormData((f) => ({ ...f, activities: items }))
									}
									fetchAll={() =>
										activitiesApi
											.listPaginated(token, { limit: 999999 })
											.then((res) => res.data)
									}
									isLinked={(a) => !!a.project?.id}
									columns={[
										{ key: 'id', label: 'ID' },
										{ key: 'beginDate', label: 'Date' },
										{ key: 'origin', label: 'Origine' },
										{ key: 'service', label: 'Service' },
										{
											key: 'durationHours',
											label: 'Durée',
											render: (_value: unknown, item: ActivityData) => {
												if (item.durationHours == null) return '—';
												const totalMinutes = Math.round(
													item.durationHours * 60
												);
												const hours = Math.floor(totalMinutes / 60);
												const minutes = totalMinutes % 60;
												return minutes > 0
													? `${hours}h${String(minutes).padStart(2, '0')}`
													: `${hours}h`;
											},
										},
										{ key: 'unitPriceExclTax', label: 'Tarif HT' },
										{
											key: 'customerInvoice',
											label: 'Facture',
											render: (_value: unknown, item: ActivityData) => {
												if (!item.customerInvoice?.id) return '—';
												return (
													<a
														href={`/app/CustomerInvoices/${item.customerInvoice.id}/edit`}
														className="text-gray-600 hover:text-blue-600 hover:underline cursor-pointer"
														onClick={(e) => e.stopPropagation()}
													>
														{item.customerInvoice.referenceNumber ??
															`#${item.customerInvoice.id}`}
													</a>
												);
											},
										},
										{ key: 'badgeMovements', label: 'Propriétaire' },
									]}
									linkedTitle="Liées"
									unlinkedTitle="Disponibles"
									idUrlPrefix="/app/activities"
									linkedFooterTotals={activityFooterTotals}
								/>
							);
						})()}
					</CardSection>

					{/* TABLEAU DES ÉLÉMENTS LOGISTIQUES LIÉS A L'AFFAIRE */}
					<CardSection icon={faClipboardList} title="Éléments logistiques">
						<RelatedItemsManager<LogisticElementData>
							linkedItems={formData.logisticElements || []}
							setLinkedItems={(items) =>
								setFormData((f) => ({ ...f, logisticElements: items }))
							}
							fetchAll={() =>
								logisticElementsApi
									.listPaginated(token, { limit: 999999 })
									.then((res) => res.data)
							}
							isLinked={(le) => !!le.project?.id}
							columns={[
								{ key: 'id', label: 'ID' },
								{ key: 'date', label: 'Date' },
								{ key: 'product', label: 'Produit' },
								{ key: 'service', label: 'Service' },
								{ key: 'unitPriceExclTax', label: 'Tarif HT' },
								{ key: 'quantity', label: 'Quantité' },
								{ key: 'customerInvoice', label: 'Facture' },
							]}
							linkedTitle="Liés"
							unlinkedTitle="Les éléments logistiques ne peuvent pas être déliés."
							readOnly={true}
							readOnlyTooltip="Les éléments logistiques sont toujours rattachés à une affaire et se gèrent via le menu dédié."
							idUrlPrefix="/app/logisticElements"
							linkedFooterTotals={(() => {
								const totalHT = (formData.logisticElements || []).reduce(
									(sum, le) =>
										sum + (le.unitPriceExclTax ?? 0) * (le.quantity ?? 0),
									0
								);
								return [
									{
										columnKey: 'unitPriceExclTax' as const,
										value:
											totalHT > 0
												? new Intl.NumberFormat('fr-FR', {
														style: 'currency',
														currency: 'EUR',
													}).format(totalHT)
												: '—',
									},
								];
							})()}
						/>
					</CardSection>
				</div>

				{/* 2 COLONNES */}
				<div className="grid grid-cols-1 md:grid-cols-2 gap-6">
					{/* TABLEAU DES COMMANDES CLIENT LIÉES À L'AFFAIRE */}
					<CardSection icon={faBox} title="Commandes client">
						<RelatedItemsManager<CustomerOrderData>
							linkedItems={formData.customerOrders || []}
							setLinkedItems={(items) =>
								setFormData((f) => ({ ...f, customerOrders: items }))
							}
							fetchAll={() =>
								customerOrdersApi
									.listPaginated(token, { limit: 999999 })
									.then((res) => res.data)
							}
							isLinked={(le) => !!le.project?.id}
							columns={[
								{ key: 'id', label: 'ID' },
								{ key: 'referenceNumber', label: 'Référence' },
								{ key: 'orderDate', label: 'Date de commande' },
								{ key: 'amount', label: 'Montant' },
							]}
							linkedTitle="Liés"
							unlinkedTitle="Disponibles"
							idUrlPrefix="/app/CustomerOrders"
							linkedFooterTotals={(() => {
								const totalHT = (formData.customerOrders || []).reduce(
									(sum, o) => sum + (o.amount ?? 0),
									0
								);
								return [
									{
										columnKey: 'amount' as const,
										value:
											totalHT > 0
												? new Intl.NumberFormat('fr-FR', {
														style: 'currency',
														currency: 'EUR',
													}).format(totalHT)
												: '—',
									},
								];
							})()}
						/>
					</CardSection>

					{/* TABLEAU DES FACTURES CLIENT LIÉES À L'AFFAIRE */}
					<CardSection icon={faFileInvoiceDollar} title="Factures client">
						<RelatedItemsManager<CustomerInvoiceData>
							linkedItems={formData.customerInvoices || []}
							setLinkedItems={(items) =>
								setFormData((f) => ({ ...f, customerInvoices: items }))
							}
							fetchAll={() =>
								customerInvoicesApi
									.listPaginated(token, { limit: 999999 })
									.then((res) => res.data)
							}
							isLinked={(a) => !!a.project?.id}
							columns={[
								{ key: 'id', label: 'ID' },
								{ key: 'referenceNumber', label: 'Référence' },
								{ key: 'invoiceDate', label: 'Date de facture' },
								{
									key: 'totalExclTax',
									label: 'Montant HT',
									render: (val) =>
										new Intl.NumberFormat('fr-FR', {
											style: 'currency',
											currency: 'EUR',
										}).format(Number(val ?? 0)),
								},
							]}
							linkedTitle="Liés"
							unlinkedTitle="Disponibles"
							readOnly={true}
							readOnlyTooltip="Les factures client sont toujours rattachées à une affaire et se gèrent via le menu dédié."
							idUrlPrefix="/app/CustomerInvoices"
							linkedFooterTotals={(() => {
								const invoices = formData.customerInvoices ?? [];
								if (invoices.length === 0) return [];
								const totalFacture = invoices.reduce(
									(sum, inv) => sum + Number(inv.totalExclTax ?? 0),
									0
								);
								const fmtEur = (v: number) =>
									new Intl.NumberFormat('fr-FR', {
										style: 'currency',
										currency: 'EUR',
									}).format(v);
								return [
									{ columnKey: 'id', value: '' },
									{ columnKey: 'referenceNumber', value: '' },
									{ columnKey: 'invoiceDate', value: '' },
									{ columnKey: 'totalExclTax', value: fmtEur(totalFacture) },
								] satisfies FooterTotal<CustomerInvoiceData>[];
							})()}
						/>
					</CardSection>
				</div>

				{/* 2 COLONNES */}
				<div className="grid grid-cols-1 md:grid-cols-2 gap-6">
					{/* TABLEAU DES ACOMPTES LIÉS À L'AFFAIRE */}
					<CardSection icon={faMoneyBills} title="Acomptes">
						<RelatedItemsManager<AdvanceData>
							linkedItems={formData.advances || []}
							setLinkedItems={(items) =>
								setFormData((f) => ({ ...f, advances: items }))
							}
							fetchAll={() =>
								advancesApi
									.listPaginated(token, { limit: 999999 })
									.then((res) => res.data)
							}
							isLinked={(le) => !!le.project?.id}
							columns={[
								{ key: 'id', label: 'ID' },
								{ key: 'referenceNumber', label: 'Référence' },
								{ key: 'amount', label: 'Montant HT' },
							]}
							linkedTitle="Liés"
							unlinkedTitle="Disponibles"
							idUrlPrefix="/app/advances"
							linkedFooterTotals={(() => {
								const totalHT = (formData.advances || []).reduce(
									(sum, a) => sum + (a.amount ?? 0),
									0
								);
								return [
									{
										columnKey: 'amount' as const,
										value:
											totalHT > 0
												? new Intl.NumberFormat('fr-FR', {
														style: 'currency',
														currency: 'EUR',
													}).format(totalHT)
												: '—',
									},
								];
							})()}
						/>
					</CardSection>
				</div>

				{/* TABLEAU DES DÉPENSES À REFACTURER */}
				{/* Toujours affiché. Indique que l'affaire doit être enregistrée (création) ou qu'il n'y a aucune dépense éligible. */}
				<div className="grid grid-cols-1 gap-6">
					<CardSection
						icon={faReceipt}
						title="Dépenses à refacturer"
						className="md:col-span-3"
					>
						{!id ? (
							// Cas création : l'affaire n'est pas encore enregistrée
							<p className="text-sm text-gray-400 italic">
								Enregistrez l&apos;affaire pour consulter les dépenses à
								refacturer.
							</p>
						) : (
							// Cas édition : filtre les dépenses liées à une facture fournisseur avec P&S > 0
							<>
								<p className="text-sm text-gray-400 italic">
									Créer un Elément Logistique (EL) pour chaque dépense à
									refacturer.
									<br />
									Le bouton d&apos;action tout à droite de chaque dépense vous
									permet de créer cet EL automatiquement avec le montant
									refacturable.
								</p>
								{(() => {
									const refacturableExpenses = (formData.expenses ?? []).filter(
										(e) =>
											e.supplierInvoice !== null &&
											e.supplierInvoice !== undefined &&
											Number(e.supplierInvoice.penaltyAndCarePercent ?? 0) > 0
									);

									if (refacturableExpenses.length === 0) {
										return (
											<p className="text-sm text-gray-400 italic">
												Aucune dépense à refacturer sur cette affaire.
											</p>
										);
									}

									return (
										<RelatedItemsTable
											title=""
											items={refacturableExpenses}
											columns={[
												{
													key: 'id',
													label: 'ID',
													render: (_val, expense) => (
														<a
															href={`/app/expenses/${expense.id}/edit`}
															onClick={(e) => {
																e.preventDefault();
																navigate(`/app/expenses/${expense.id}/edit`);
															}}
															className="text-gray-500 hover:text-blue-600 hover:underline"
														>
															#{expense.id}
														</a>
													),
												},
												{ key: 'label', label: 'Libellé' },
												{
													key: 'supplierInvoice',
													label: 'Facture fournisseur',
													render: (_val, expense) =>
														expense.supplierInvoice ? (
															<a
																href={`/app/supplierInvoices/${expense.supplierInvoice.id}/edit`}
																onClick={(e) => {
																	e.preventDefault();
																	navigate(
																		`/app/supplierInvoices/${expense.supplierInvoice?.id}/edit`
																	);
																}}
																className="text-gray-500 hover:text-blue-600 hover:underline"
															>
																{expense.supplierInvoice.referenceNumber}
															</a>
														) : (
															'—'
														),
												},
												{
													key: 'amountHtInvoiced',
													label: 'Montant HT',
													render: (_val, expense) =>
														new Intl.NumberFormat('fr-FR', {
															style: 'currency',
															currency: 'EUR',
														}).format(Number(expense.amountHtInvoiced ?? 0)),
												},
												{
													key: 'logisticElement',
													label: 'P&S',
													render: (_val, expense) =>
														`${Number(expense.supplierInvoice?.penaltyAndCarePercent ?? 0).toLocaleString('fr-FR')} %`,
												},
												{
													key: 'id',
													label: 'Refacturable',
													render: (_val, expense) => {
														const base = Number(expense.amountHtInvoiced ?? 0);
														const pct = Number(
															expense.supplierInvoice?.penaltyAndCarePercent ??
																0
														);
														return new Intl.NumberFormat('fr-FR', {
															style: 'currency',
															currency: 'EUR',
														}).format(base * (1 + pct / 100));
													},
												},
												{
													key: 'logisticElement',
													label: 'Montant EL',
													render: (_val, expense) => {
														const el = expense.logisticElement;
														if (!el) return '—';
														const price = Number(el.unitPriceExclTax ?? 0);
														const qty = Number(el.quantity ?? 0);
														return new Intl.NumberFormat('fr-FR', {
															style: 'currency',
															currency: 'EUR',
														}).format(price * qty);
													},
												},
												{
													key: 'logisticElement',
													label: 'EL',
													render: (_val, expense) => {
														const el = expense.logisticElement;
														if (!el) return '—';
														return (
															<a
																href={`/app/logisticElements/${el.id}/edit`}
																onClick={(e) => {
																	e.preventDefault();
																	navigate(
																		`/app/logisticElements/${el.id}/edit`
																	);
																}}
																className="text-blue-600 hover:underline font-medium"
															>
																#{el.id}
															</a>
														);
													},
												},
												{
													key: 'logisticElement',
													label: 'Statut',
													render: (_val, expense) => {
														if (!expense.logisticElement) {
															return (
																<span className="text-orange-500">
																	⏳ EL à créer
																</span>
															);
														}
														if (expense.logisticElement.customerInvoice) {
															// L'ÉL est rattaché à une facture client → considéré comme facturé
															return (
																<a
																	href={`/app/customerInvoices/${expense.logisticElement.customerInvoice.id}/edit`}
																	onClick={(e) => {
																		e.preventDefault();
																		navigate(
																			`/app/customerInvoices/${expense.logisticElement?.customerInvoice?.id}/edit`
																		);
																	}}
																	className="text-gray-500 hover:text-blue-600 hover:underline"
																>
																	✅ Facturé (
																	{expense.logisticElement.customerInvoice
																		.referenceNumber ??
																		`FA #${expense.logisticElement.customerInvoice.id}`}
																	)
																</a>
															);
														}
														// L'ÉL existe mais n'est pas encore facturé
														return (
															<a
																href={`/app/logisticElements/${expense.logisticElement.id}/edit`}
																onClick={(e) => {
																	e.preventDefault();
																	navigate(
																		`/app/logisticElements/${expense.logisticElement?.id}/edit`
																	);
																}}
																className="text-gray-500 hover:text-blue-600 hover:underline"
															>
																🔗 ÉL #{expense.logisticElement.id} (prêt à
																facturer)
															</a>
														);
													},
												},
											]}
											actionIcon={faClipboardList}
											actionColorClass="text-blue-600 hover:text-blue-800"
											onAction={(expense) => {
												if (!expense.logisticElement) {
													void handleCreateRefacturation(expense);
												}
											}}
											actionDisabled={(expense) =>
												!!expense.logisticElement ||
												refacturationInProgress === expense.id
											}
											actionTooltip={(expense) =>
												expense.logisticElement?.customerInvoice
													? 'Déjà facturé au client'
													: expense.logisticElement
														? 'Élément logistique déjà créé (prêt à facturer)'
														: refacturationInProgress === expense.id
															? 'Refacturation en cours…'
															: 'Créer un élément logistique de refacturation'
											}
											emptyLabel="Aucune dépense à refacturer."
											readOnly={false}
											readOnlyTooltip=""
										/>
									);
								})()}

								{/* Légende du tableau */}
								<p className="text-xs text-gray-400 italic mt-2">
									Ces dépenses sont liées à une facture fournisseur comportant
									un pourcentage de peines &amp; soins {'>'} 0. Utilisez le
									bouton d&apos;action pour créer automatiquement un élément
									logistique de refacturation au tarif majoré.
								</p>
							</>
						)}
					</CardSection>
				</div>

				{/* TABLEAU DES COMMANDES FOURNISSEUR LIÉES (lecture seule, calculé depuis les lignes de commande) */}
				<div className="grid grid-cols-1 gap-6">
					<CardSection
						icon={faBox}
						title="Commandes fournisseur liées"
						className="md:col-span-3"
					>
						{(() => {
							const orders = formData.linkedSupplierOrders ?? [];
							const totalHT = orders.reduce(
								(sum, o) => sum + Number(o.latestVersionTotalExclTax ?? 0),
								0
							);
							const supplierOrderFooterTotals: FooterTotal<
								NonNullable<ProjectData['linkedSupplierOrders']>[number]
							>[] =
								orders.length > 0
									? [
											{ columnKey: 'referenceNumber', value: '' },
											{ columnKey: 'tiersLabel', value: '' },
											{ columnKey: 'latestVersionStatus', value: '' },
											{
												columnKey: 'latestVersionTotalExclTax',
												value: new Intl.NumberFormat('fr-FR', {
													style: 'currency',
													currency: 'EUR',
												}).format(totalHT),
											},
										]
									: [];
							return (
								<RelatedItemsTable
									title="Commandes fournisseur"
									items={orders}
									columns={[
										{
											key: 'referenceNumber',
											label: 'Référence',
											// Lien cliquable vers la dernière version valide de la commande
											render: (_val, order) =>
												order.latestVersionId ? (
													<button
														type="button"
														className="text-blue-600 hover:underline font-medium"
														onClick={() =>
															navigate(
																`/app/orderVersions/${order.latestVersionId}/manage`
															)
														}
													>
														{order.referenceNumber}
													</button>
												) : (
													<span>{order.referenceNumber}</span>
												),
										},
										{ key: 'tiersLabel', label: 'Fournisseur' },
										{ key: 'latestVersionStatus', label: 'Statut' },
										{
											key: 'latestVersionTotalExclTax',
											label: 'Montant HT',
											render: (_val, order) =>
												new Intl.NumberFormat('fr-FR', {
													style: 'currency',
													currency: 'EUR',
												}).format(Number(order.latestVersionTotalExclTax ?? 0)),
										},
									]}
									actionIcon={faBox}
									actionColorClass=""
									onAction={() => {
										/* lecture seule — non utilisé */
									}}
									emptyLabel="Aucune commande fournisseur liée à cette affaire."
									readOnly
									readOnlyTooltip="Les commandes fournisseur sont liées automatiquement via leurs lignes de commande."
									footerTotals={supplierOrderFooterTotals}
								/>
							);
						})()}
					</CardSection>
				</div>
			</form>

			<FormSaveActions
				isDirty={isDirty}
				isSubmitting={isSubmitting}
				onCancel={() => navigate('/app/projects')}
				onSave={() => handleSave(false)}
				onSaveAndLeave={() => handleSave(true)}
			/>
		</div>
	);
};

export default ProjectForm;
